import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is an npm package, not a local convex/ directory file
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ are one level deeper than src/pages/, requiring three levels up to reach convex/_generated/
import { useOrgContext } from '@/hooks/useOrgContext';
import { useClaimFilters } from '@/hooks/useClaimFilters';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { useTableSort } from '@/hooks/useTableSort';
import { ColumnSettingsPopover } from '@/components/insurance/ColumnSettingsPopover';
import { SortableColumnHeader } from '@/components/insurance/SortableColumnHeader';
import { CLAIMS_COLUMNS } from '@/lib/tableColumns';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IClaim, TClaimStatus, IClaimStatusBadgeProps } from '@/@types/claims';
import type { IColumnConfig } from '@/@types/tableColumnVisibility';

function formatCurrency(amount: number, currency: string | undefined) {
  if (!currency) return `${amount}`;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(ts: number) {
  return new Date(ts).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function ClaimStatusBadge({ status }: IClaimStatusBadgeProps) {
  return (
    <Badge
      variant="outline"
      className={cn(
        'capitalize whitespace-nowrap',
        status === 'submitted' && 'bg-blue-100 text-blue-800 border-blue-200',
        status === 'under_review' && 'bg-yellow-100 text-yellow-800 border-yellow-200',
        status === 'additional_info_required' && 'bg-orange-100 text-orange-800 border-orange-200',
        status === 'approved' && 'bg-green-100 text-green-800 border-green-200',
        status === 'partially_approved' && 'bg-lime-100 text-lime-800 border-lime-200',
        status === 'rejected' && 'bg-red-100 text-red-800 border-red-200',
        status === 'paid' && 'bg-emerald-100 text-emerald-800 border-emerald-200',
        status === 'partially_paid' && 'bg-teal-100 text-teal-800 border-teal-200'
      )}
    >
      {status.replace(/_/g, ' ')}
    </Badge>
  );
}

function ClaimsTableSkeleton({ colCount }: { colCount: number }) {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <TableRow key={i}>
          {Array.from({ length: colCount }).map((__, j) => (
            <TableCell key={j}>
              <Skeleton className="h-4 w-24" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function ClaimsEmptyState({ hasFilters, colCount }: { hasFilters: boolean; colCount: number }) {
  return (
    <TableRow>
      <TableCell colSpan={colCount}>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <FileText className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No claims found</h3>
          <p className="text-sm text-muted-foreground">
            {hasFilters
              ? 'Try adjusting your filters or search query'
              : 'No claims have been submitted yet'}
          </p>
        </div>
      </TableCell>
    </TableRow>
  );
}

function renderClaimCell(colId: string, claim: IClaim): React.ReactNode {
  switch (colId) {
    case 'claimNumber':
      return <span className="font-medium">{claim.claimNumber}</span>;
    case 'claimant':
      return (
        <span className="text-muted-foreground">{claim.claimantName ?? claim.claimantId}</span>
      );
    case 'treatmentType':
      return (
        <span className="text-muted-foreground capitalize">
          {claim.treatmentType?.replace(/_/g, ' ') ?? '—'}
        </span>
      );
    case 'amount':
      return (
        <span className="text-muted-foreground">
          {formatCurrency(claim.totalAmountClaimed, claim.currency)}
        </span>
      );
    case 'status':
      // AS_JUSTIFIED: status string from Convex return narrowed to TClaimStatus union
      return <ClaimStatusBadge status={claim.status as TClaimStatus} />; // AS_JUSTIFIED: status string from Convex return narrowed to TClaimStatus union
    case 'dateOfService':
      return <span className="text-muted-foreground">{formatDate(claim.dateOfService)}</span>;
    case 'provider':
      return <span className="text-muted-foreground">{claim.providerName ?? '—'}</span>;
    case 'created':
      return <span className="text-muted-foreground">{formatDate(claim.createdAt)}</span>;
    default:
      return null;
  }
}

function ClaimsTableRow({
  claim,
  visibleColumns,
  onClick,
}: {
  claim: IClaim;
  visibleColumns: IColumnConfig[];
  onClick: () => void;
}) {
  return (
    <TableRow className="cursor-pointer hover:bg-muted/50" onClick={onClick}>
      {visibleColumns.map(col => (
        <TableCell
          key={col.id}
          className={cn(col.pinned === 'left' && 'sticky left-0 z-10 bg-background')}
        >
          {renderClaimCell(col.id, claim)}
        </TableCell>
      ))}
    </TableRow>
  );
}

export function ClaimsListPage() {
  const navigate = useNavigate();
  const { currentOrg, isLoading: orgLoading } = useOrgContext();

  const rawClaims = useQuery(
    api.insurance.claims.list,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );

  const isLoading = orgLoading || rawClaims === undefined;
  const claims: IClaim[] = (rawClaims ?? []) as IClaim[]; // AS_JUSTIFIED: Convex return narrowed to typed IClaim[] matching return validator

  const { filters, filteredClaims, dispatchFilter } = useClaimFilters(claims);

  const claimAccessor = useCallback((claim: IClaim, columnId: string): string | number => {
    switch (columnId) {
      case 'claimNumber':
        return claim.claimNumber;
      case 'claimant':
        return claim.claimantName ?? claim.claimantId;
      case 'treatmentType':
        return claim.treatmentType ?? '';
      case 'amount':
        return claim.totalAmountClaimed ?? 0;
      case 'status':
        return claim.status;
      case 'dateOfService':
        return claim.dateOfService ?? 0;
      case 'provider':
        return claim.providerName ?? '';
      case 'created':
        return claim.createdAt;
      default:
        return '';
    }
  }, []);

  const {
    sortState,
    toggleSort,
    sortedItems: sortedClaims,
  } = useTableSort(
    filteredClaims,
    CLAIMS_COLUMNS,
    { defaultSort: { sortBy: 'created', direction: 'desc' } },
    claimAccessor
  );

  const { visibleColumns, allColumns, isColumnVisible, toggleColumn, resetToDefaults } =
    useColumnVisibility('insurance-claims', CLAIMS_COLUMNS);

  const hasActiveFilters =
    filters.search !== '' ||
    filters.status !== 'all' ||
    filters.treatmentType !== 'all' ||
    filters.dateRange !== 'all';

  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <FileText className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to manage claims.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Claims</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isLoading ? 'Loading...' : `${claims.length} total`}
          </p>
        </div>
      </div>

      <div className="bg-table-container border border-table-container-border rounded-none overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-6 py-3 border-b border-table-row-border">
          <label htmlFor="claim-search" className="sr-only">
            Search claims
          </label>
          <Input
            id="claim-search"
            placeholder="Search by claim # or provider..."
            value={filters.search}
            onChange={e => dispatchFilter({ type: 'SET_SEARCH', query: e.target.value })}
            className="max-w-xs"
          />
          <label htmlFor="claim-status-filter" className="sr-only">
            Filter by status
          </label>
          <Select
            value={filters.status}
            onValueChange={val => dispatchFilter({ type: 'SET_STATUS', status: val })}
          >
            <SelectTrigger id="claim-status-filter" className="w-[180px]">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="under_review">Under Review</SelectItem>
              <SelectItem value="additional_info_required">Additional Info Required</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="partially_approved">Partially Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="partially_paid">Partially Paid</SelectItem>
            </SelectContent>
          </Select>
          <label htmlFor="claim-type-filter" className="sr-only">
            Filter by treatment type
          </label>
          <Select
            value={filters.treatmentType}
            onValueChange={val =>
              dispatchFilter({ type: 'SET_TREATMENT_TYPE', treatmentType: val })
            }
          >
            <SelectTrigger id="claim-type-filter" className="w-[180px]">
              <SelectValue placeholder="All treatment types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All treatment types</SelectItem>
              <SelectItem value="outpatient">Outpatient</SelectItem>
              <SelectItem value="dental">Dental</SelectItem>
              <SelectItem value="optical">Optical</SelectItem>
              <SelectItem value="maternity">Maternity</SelectItem>
              <SelectItem value="inpatient">Inpatient</SelectItem>
              <SelectItem value="wellness">Wellness</SelectItem>
            </SelectContent>
          </Select>
          <label htmlFor="claim-date-filter" className="sr-only">
            Filter by date range
          </label>
          <Select
            value={filters.dateRange}
            onValueChange={val => dispatchFilter({ type: 'SET_DATE_RANGE', dateRange: val })}
          >
            <SelectTrigger id="claim-date-filter" className="w-[160px]">
              <SelectValue placeholder="All dates" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All dates</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <div className="ml-auto">
            <ColumnSettingsPopover
              allColumns={allColumns}
              isColumnVisible={isColumnVisible}
              toggleColumn={toggleColumn}
              resetToDefaults={resetToDefaults}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table className="min-w-max">
            <TableHeader>
              <TableRow>
                {visibleColumns.map(col => (
                  <TableHead
                    key={col.id}
                    className={cn(col.pinned === 'left' && 'sticky left-0 z-10 bg-table-header')}
                  >
                    <SortableColumnHeader
                      column={col}
                      sortState={sortState}
                      onToggleSort={toggleSort}
                    />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <ClaimsTableSkeleton colCount={visibleColumns.length} />}
              {!isLoading &&
                sortedClaims.map(claim => (
                  <ClaimsTableRow
                    key={claim._id}
                    claim={claim}
                    visibleColumns={visibleColumns}
                    onClick={() => navigate(`/insurance/claims/${claim._id}`)}
                  />
                ))}
              {!isLoading && sortedClaims.length === 0 && (
                <ClaimsEmptyState hasFilters={hasActiveFilters} colCount={visibleColumns.length} />
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
